/**
 * Date utility functions for formatting dates consistently across the application
 */
import moment from 'moment';

type DateInput = string | Date | null | undefined;

/**
 * Format a date as a date and time string (DD.MM.YYYY HH:mm)
 * @param {string|Date} dateTime - The date to format
 * @param {string} format - Optional custom format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateTime: DateInput, format = 'DD.MM.YYYY HH:mm'): string => {
  if (!dateTime) return '';
  return moment(dateTime).format(format);
};

/**
 * Format a date as a date string (DD.MM.YYYY)
 * @param {string|Date} date - The date to format
 * @param {string} format - Optional custom format
 * @returns {string} Formatted date string
 */
export const formatDate = (date: DateInput, format = 'DD.MM.YYYY'): string => {
  if (!date) return '';
  return moment(date).format(format);
};

/**
 * Format a date as a time string (HH:mm)
 * @param {string|Date} time - The time to format
 * @param {string} format - Optional custom format
 * @returns {string} Formatted time string
 */
export const formatTime = (time: DateInput, format = 'HH:mm'): string => {
  if (!time) return '';
  return moment(time).format(format);
};

/**
 * Get a relative time string (e.g., "2 hours ago", "in 3 days")
 * @param {string|Date} date - The date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date: DateInput): string => {
  if (!date) return '';
  return moment(date).fromNow();
};

/**
 * Check if a date is in the past
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is in the past
 */
export const isPast = (date: DateInput): boolean => {
  if (!date) return false;
  return moment(date).isBefore(moment());
};

/**
 * Check if a date is in the future
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is in the future
 */
export const isFuture = (date: DateInput): boolean => {
  if (!date) return false;
  return moment(date).isAfter(moment());
};

/**
 * Format a duration in hours
 * @param {number} hours - Duration in hours
 * @returns {string} Formatted duration string
 */
export const formatDuration = (hours: number): string => {
  const duration = moment.duration(hours, 'hours');
  const days = Math.floor(duration.asDays());
  const remainingHours = duration.hours();

  if (days > 0) {
    return `${days} ${days === 1 ? 'den' : 'dní'} ${remainingHours > 0 ? `${remainingHours} ${remainingHours === 1 ? 'hodina' : remainingHours < 5 ? 'hodiny' : 'hodin'}` : ''}`;
  }

  return `${hours} ${hours === 1 ? 'hodina' : hours < 5 ? 'hodiny' : 'hodin'}`;
};
